import { createSelector } from 'reselect';
import { signInCheck } from '../../components/common/CheckUserStatus';
import { extractVariantsFromProduct, getSizeName } from '../utils/productUtils';
import { getWishlistData } from './wishlistSelector';
import { beautyCheck, fetchSizeAndBrandSize } from '../../utils/StandardSizeUtils';
import properties from '../../modules/ajio-util/properties';
import { getLuxeFlags } from './featureFlagsSelector';
import { getCurrentStore } from './metaData';
import * as Constants from './constants';

const getCart = (state: import('../types/rootState').RootState): import('../types/cart').Cart =>
  state.cart;

const getState = (state: import('../types/rootState').RootState):  import('../types/rootState').RootState =>
state;
const getAssuredData = (
  state: import('../types/rootState').RootState
): import('../types/cart').Cart => state;

const getVoucherData = (
  state: import('../types/rootState').RootState
): import('../types/voucher').Voucher => state.voucher || {};

const getDeliveryAddress = createSelector(getCart, cart => cart?.deliveryInfo?.deliveryAddress || {});
const getOosStatus = createSelector(getCart, cart => cart?.oosStatus);

const getCartLimitDetails = createSelector(getCart, ({ cartValueLimit = {} }) => {
  const { errorMessage = '', reason = '' } = cartValueLimit;
  return {
    hasLimitExceed: reason === 'MAX_CART_VALUE_LIMIT',
    errorMessage
  };
});
const getCartLimitDetailsNew = createSelector(getCart, ({ cartValueLimitNew = {} }) => {
  const { errorMessage = '', reason = '' } = cartValueLimitNew;
  return {
    hasLimitExceed: reason === 'MAX_CART_VALUE_LIMIT',
    errorMessage
  };
});
const getPageBanner = createSelector(getCart, cart => cart.pageBanner || '');
const getTotalUnitCount = createSelector(getCart, cart => cart.totalUnitCount || 0);

const getTotalUintWithGWP = createSelector(getState, state => {
  const {enableGiftWithProduct = 'OFF'} = state.flags;
  const {cartItem = [], giftItem = []} = state;
  const cartItemCount = cartItem.reduce((acc, curr) => {
    return acc + (curr?.quantity ? curr?.quantity : 0)
  }, 0)
  if(enableGiftWithProduct === 'ON'){
    const giftItemCount = giftItem.reduce((acc, curr):number => {
      return acc + (curr?.quantity ? curr?.quantity : 0)
    }, 0);
    return cartItemCount + giftItemCount;
  }else{
    return cartItemCount
  }
});

const getDeliveryFeeThresholdAmount = createSelector(
  getCart,
  cart => cart.deliveryFeeThresholdAmount
);
const getTotalProductCount = createSelector(getCart, cart => cart.totalItems || 0);
const getMembershipBenefitResponse = createSelector(getState, state => {
  const { cart, membership } = state;
  return cart?.membershipBenefitResponse || (membership?.membershipData?.status ? membership?.membershipData : null)
});
const getMembershipBenefitList = createSelector(getCart, cart => cart?.orderSummary?.memberShipBenefitList || null)

const getCartError = createSelector(getCart, cart => cart.errorCode || '');
const getOrderSummary = createSelector(getCart, cart => cart.orderSummary || {});
const getDeepLinkingResponse = createSelector(
  getCart,
  cart => cart.deepLinkingOosItemList || { productIds: [], message: '' }
);
const getOrderTotal = createSelector(
  getOrderSummary,
  orderSummary => orderSummary?.orderTotal || {}
);
const getOrderTotalValue = createSelector(getOrderTotal, orderTotal => orderTotal.value || 0);
const getDeliveryChargeValue = createSelector(
  getOrderSummary,
  orderSummary => orderSummary?.deliveryCharge?.value || 0
);
const getGstChargeValue = createSelector(
  getOrderSummary,
  orderSummary => orderSummary?.exclusiveGSTCharge?.value || 0
);

const getSizeDetails = createSelector(getCart, cart => cart?.sizeDetails);

const getSizeOptions = createSelector(getSizeDetails, sizeDetails => {
  if (sizeDetails) {
    const { sizeVariant: variantOptions = [] } = extractVariantsFromProduct(sizeDetails);
    return variantOptions?.reduce<Record<string, string | boolean>[]>(
      (acc, { variantOptionQualifiers, code, stock: { stockLevelStatus, stockLevel } }) => {
        const fallback_size = getSizeName({ variantOptionQualifiers });
        const { valueWithUnit: beauty } = beautyCheck(variantOptionQualifiers, true);
        const { Size = '', sizeType = '' } = fetchSizeAndBrandSize(variantOptionQualifiers);
        const sizeName = beauty || Size || fallback_size;
        return [
          ...acc,
          {
            value: sizeName,
            label: sizeName,
            code,
            disabled: stockLevelStatus === 'outOfStock' || stockLevel === 0,
            stockLevel,
            sizeFormat: sizeType
          }
        ];
      },
      []
    );
  }
  return [];
});

const getDealExpireDescription = createSelector(
  getCart,
  cart => cart.dealExpirePayment?.description || ''
);

const getDeliveryChangeAlert = createSelector(getState, state =>{
  const {enablePriorityDeliveryPreOrder, priorityDeliveryConfig={} }  = state.flags;
  const {enablerushdelivery = {}, rushdeliveryerrormsgs={}} = state?.variants;
  const {priorityDeliveryAlertMessage = '', errorMessages=[]}  = state.cart;
  const rushDelivery = enablerushdelivery.key?.toUpperCase() === 'ON' ? enablerushdelivery?.payload : false;
  const rushDeliveryAlertConfig = errorMessages.find((msgObj) => msgObj.code === 'RUSH_DETAILS_CHANGED') || {};
  const priorityDeliveryAlertConfig = errorMessages.find((msgObj) => msgObj.code === 'PD_DETAILS_CHANGED') || {};
  const latLongAlert = errorMessages.find((msgObj) => msgObj.code === 'RUSH_LAT_LONG_REQUIRED') || {};

  const rushDeliveryAlertMessage = rushdeliveryerrormsgs?.key?.toUpperCase() === "ON" && rushdeliveryerrormsgs?.payload ? rushdeliveryerrormsgs?.payload.find((msgObj) => msgObj.code === 'RUSH_DETAILS_CHANGED') || {} : {};
  const newPriorityDeliveryAlertMessage = rushdeliveryerrormsgs?.key?.toUpperCase() === "ON" && rushdeliveryerrormsgs?.payload ? rushdeliveryerrormsgs?.payload.find((msgObj) => msgObj.code === 'PD_DETAILS_CHANGED') || {} : {};
  const locationAlertMessage = rushdeliveryerrormsgs?.key?.toUpperCase() === "ON" && rushdeliveryerrormsgs?.payload ? rushdeliveryerrormsgs?.payload.find((msgObj) => msgObj.code === 'RUSH_LAT_LONG_REQUIRED') || {} : {};

  const rushTitle = rushDeliveryAlertMessage?.title ?? '';
  const rushMsg = rushDeliveryAlertMessage?.msg?.replace('{productsCount}', rushDeliveryAlertConfig?.placeHolders?.productCount ?? '') ?? '';
  const pdTitle = newPriorityDeliveryAlertMessage?.title ?? '';
  const pdMsg = newPriorityDeliveryAlertMessage?.msg?.replace('{productsCount}', priorityDeliveryAlertConfig?.placeHolders?.productCount ?? '') ?? '';
  const latLongTitle = locationAlertMessage.title;
  const latLongMsg = locationAlertMessage.msg;
  const isRushChange = Object.keys(rushDeliveryAlertConfig).length > 0;
  const isPDChange = Object.keys(priorityDeliveryAlertConfig).length > 0;
  const isLatLongChange = Object.keys(latLongAlert).length > 0;

  if (enablePriorityDeliveryPreOrder === 'OFF' || (!rushDelivery && !priorityDeliveryAlertMessage) || (!isRushChange && !isPDChange && !isLatLongChange && !priorityDeliveryAlertMessage)) {
    return {};
  }

  return {
    title: rushDelivery ? (isRushChange ? rushTitle : (isPDChange ? pdTitle : isLatLongChange ? latLongTitle : '')) : priorityDeliveryAlertMessage ? priorityDeliveryConfig?.PDAlertTitle : '',
    description: rushDelivery ? (isRushChange ? rushMsg : (isPDChange ? pdMsg : isLatLongChange ? latLongMsg : '')) : priorityDeliveryAlertMessage,
    isPDIcon: isPDChange,
    isRushIcon: isRushChange || isLatLongChange,
    isRushPDIcon: isPDChange && (isRushChange || isLatLongChange)
  }
})

const getRushDeliveryBannerErrorMsgs = createSelector(getState, state =>{
  const { rushdeliveryerrormsgs={} }  = state?.variants;
  const { errorMessages = [] }  = state.cart;

  const priorityCodes = [
    'RUSH_AND_PD_MAX_CAPPING_REACHED',
    'RUSH_MAX_CAPPING_REACHED',
    'PD_MAX_CAPPING_REACHED'
  ];
  
  let rushDeliveryAlertConfig = {};
  
  for (const code of priorityCodes) {
    const match = errorMessages.find(msgObj => msgObj.code === code);
    if (match) {
      rushDeliveryAlertConfig = match;
      break;
    }
  }

  const rushDeliveryAlertMessage = rushdeliveryerrormsgs?.key?.toUpperCase() === "ON" && rushdeliveryerrormsgs?.payload ? rushdeliveryerrormsgs?.payload.find((msgObj) => msgObj.code === rushDeliveryAlertConfig.code) || {} : {};

  const rushTitle = rushDeliveryAlertMessage?.title ?? '';
  const rushMsg = rushDeliveryAlertMessage?.msg ?? '';

  return {
    title: rushTitle,
    description: rushMsg,
    exists: Object.keys(rushDeliveryAlertConfig).length > 0,
    isPD: rushDeliveryAlertConfig.code === 'PD_MAX_CAPPING_REACHED',
    isRush: rushDeliveryAlertConfig.code === 'RUSH_MAX_CAPPING_REACHED',
    isRushPDCombined: rushDeliveryAlertConfig.code === 'RUSH_AND_PD_MAX_CAPPING_REACHED'
  }
})

const filterImage = (images: import('../types/commontypes').Image[]): string | undefined =>
  images.find(image => image.format === 'cartIcon' || image.format === 'PRIMARY')?.url;

const getRestoredProducts = createSelector(getCart, cart =>
  cart?.restoredProducts?.products?.map(({ code, images }) => {
    return {
      code,
      image: filterImage(images)
    };
  })
);

const getLimitedStockProducts = createSelector(getCart, cart =>
  cart?.limitedStock?.products?.map(({ code, images }) => {
    return {
      code,
      image: filterImage(images)
    };
  })
);

const getOosProducts = createSelector(getCart, cart => cart?.noStock);

const getSignInEmptyCartData = (wishlistData = []) => {
  const image = wishlistData.map((item, id) => {
    const { images = [], name: productName } = item;
    let imageUrl = '';
    images.forEach(item => {
      if (item.format == 'product') {
        imageUrl = item.url;
      } else if (item.format == 'productGrid3ListingImage') {
        imageUrl = item.url;
      } else if (item.format == 'mobileProductListingImage') {
        imageUrl = item.url;
      }
    });
    return {
      id,
      slideItem: imageUrl,
      productName
    };
  });

  return {
    isLogin: true,
    buttonLabel: 'Add From Wishlist',
    subHeader:
      image.length > 0
        ? Constants.bagEmptyDescription
        : 'Hang your wishes where you can make them come true.',
    image
  };
};

const getAnonymousEmptyCartData = luxe => ({
  isLogin: false,
  buttonLabel: luxe ? 'Login / Join' : 'Login/ Join',
  subHeader:
    'Sign in to link products to your account or view products already in your wishlist or bag',
  image: [`${properties.getProperty('imageBaseUrl')}/static/img/emptyCart.png`],
  redirectUrl: '/login?referrer=/cart'
});

const getEmptyCartData = createSelector(
  getLuxeFlags,
  getCurrentStore,
  getWishlistData,
  (luxeFlags, storeName, wishlistData) => {
    const { isThemeEnable } = luxeFlags;
    const luxe = isThemeEnable === 'ON' && storeName === 'luxe';
    const EmptyCartData = signInCheck().length
      ? getSignInEmptyCartData(wishlistData)
      : getAnonymousEmptyCartData(luxe);

    return {
      header: Constants.bagIsEmpty,
      ...EmptyCartData
    };
  }
);

const getWishlistImageArray = createSelector(getWishlistData, wishlistData => {
  const emptyCartData = getSignInEmptyCartData(wishlistData);
  const { image = [] } = emptyCartData;
  return image.map(item => item.slideItem);
});

const getCartSuccess = createSelector(getCart, ({ success = false }) => success);
const getUserType = createSelector(getCart, ({ type }) => type);

const getValidVouchers = createSelector(getVoucherData, ({ voucherList = [] }) => {
  return voucherList.length > 0
    ? voucherList.filter(voucher => voucher.isCouponValid === true)
    : [];
});

const getInvalidVouchers = createSelector(getVoucherData, ({ voucherList = {} }) => {
  return voucherList.length > 0
    ? voucherList.filter(voucher => voucher.isCouponValid === false)
    : [];
});
const getAppliedVoucherData = createSelector(getCart, ({ appliedVouchers = [] }) => {
  const voucherApplied = appliedVouchers.length > 0;
  if (voucherApplied) {
    const { voucherCode = '', valueFormatted = '', description = '' } = appliedVouchers[0];
    return {
      voucherApplied,
      voucherCode,
      valueFormatted,
      description
    };
  }
});

const getCartPriceError = createSelector(getCart, ({ serviceError = {} }) => {
  // pricing engine error
  let msgHeader = '';
  let msgDes = '';
  let cartPriceError = false;
  if (Object.keys(serviceError).length) {
    cartPriceError = true;
    msgHeader = serviceError.messageHeader;
    msgDes = serviceError.messageDescription;
  }
  return {
    cartPriceError,
    msgHeader,
    msgDes
  };
});

const getAppliedProductPromotions = createSelector(getCart, cart => cart?.appliedProductPromotions);

const getAssuredGiftData = createSelector(getAssuredData, assured => {
  const { assuredGifts: { data = {} } = {} } = assured;
  const { ctaText = '', gifts = [], hasGift = false, orderValue = 0 } = data;
  return {
    ctaText,
    gifts,
    hasGift,
    orderValue
  };
});

const getInternalWalletDetails = (
  state: import('../types/rootState').RootState
): import('../types/internalWallet').InternalWalletDetails => state.internalWalletDetails || [];

const getPanDetails = createSelector(getCart, cart => cart?.pancard);

const getActivePinCode = state => {
  const {
    cart: { deliveryInfo: { deliveryAddress: { postalCode: cartPostalCode } = {} } = {} } = {},
    order: { deliveryInfo: { deliveryAddress: { postalCode: orderPostalCode } = {} } = {} } = {},
    addressBook: { postalCode }
  } = state;
  return cartPostalCode || orderPostalCode || postalCode;
}

export {
  getOosStatus,
  getCartLimitDetails,
  getPageBanner,
  getTotalUnitCount,
  getDeliveryFeeThresholdAmount,
  getTotalProductCount,
  getCartError,
  getOrderSummary,
  getOrderTotal,
  getDeliveryChargeValue,
  getDealExpireDescription,
  getEmptyCartData,
  getWishlistImageArray,
  getCartSuccess,
  getUserType,
  getSizeOptions,
  getRestoredProducts,
  getLimitedStockProducts,
  getOosProducts,
  getVoucherData,
  getValidVouchers,
  getInvalidVouchers,
  getAppliedVoucherData,
  getGstChargeValue,
  getOrderTotalValue,
  getCartPriceError,
  getAppliedProductPromotions,
  getDeepLinkingResponse,
  getAssuredGiftData,
  getInternalWalletDetails,
  getPanDetails,
  getCartLimitDetailsNew,
  getTotalUintWithGWP,
  getDeliveryAddress,
  getDeliveryChangeAlert,
  getActivePinCode,
  getRushDeliveryBannerErrorMsgs,
  getMembershipBenefitResponse,
  getMembershipBenefitList
};
